import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Factory, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";

const CITIES = ["الرياض", "جدة", "الدمام", "الخبر", "الجبيل", "بريدة", "الطائف", "أبها", "تبوك", "حائل", "المدينة المنورة"];
const REGIONS = ["منطقة الرياض", "منطقة مكة المكرمة", "المنطقة الشرقية", "منطقة القصيم", "منطقة عسير", "منطقة تبوك", "منطقة حائل", "منطقة المدينة المنورة"];

export default function RegisterPage() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "workshop" ? "workshop_partner" : "factory_customer";
  const [role, setRole] = useState(initialRole);
  const [industries, setIndustries] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [f, setF] = useState({
    name: "", email: "", password: "", phone: "",
    company_name: "", workshop_name: "", cr_number: "", vat_number: "",
    job_title: "", industry: "", city: "", region: "",
    industrial_area: "", year_established: "", employees: "",
    capabilities: [],
  });
  const [agree, setAgree] = useState(false);
  const [attest, setAttest] = useState(false);
  const [emailType, setEmailType] = useState(null);
  const [verifyPath, setVerifyPath] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { register } = useAuth();

  useEffect(() => {
    api.get("/reference/industries").then((r) => setIndustries(r.data.industries || []));
    api.get("/reference/capabilities").then((r) => setCapabilities(r.data.capabilities || []));
  }, []);

  const upd = (k, v) => setF({ ...f, [k]: v });
  const toggleCap = (key) => {
    const has = f.capabilities.includes(key);
    upd("capabilities", has ? f.capabilities.filter((c) => c !== key) : [...f.capabilities, key]);
  };

  // Live email classification (fires when email input changes, debounced-lite)
  const onEmailChange = async (val) => {
    upd("email", val);
    if (!val || !val.includes("@") || !val.split("@")[1]?.includes(".")) {
      setEmailType(null); return;
    }
    try {
      const { data } = await api.get("/auth/classify-email", { params: { email: val } });
      setEmailType(data.email_type);
    } catch { setEmailType(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setErr("يجب الموافقة على الشروط والأحكام"); return; }
    if (!attest) { setErr("يجب الموافقة على إقرار صحة البيانات"); return; }
    setErr(""); setLoading(true);
    try {
      const payload = { ...f, role, attestation_accepted: true };
      if (payload.year_established) payload.year_established = Number(payload.year_established);
      if (payload.employees) payload.employees = Number(payload.employees);
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) {
        // Stash user in auth context by refreshing /me
      }
      toast.success("تم إنشاء الحساب بنجاح — يرجى تفعيل بريدك الإلكتروني");
      setRegisteredEmail(payload.email);
      setVerifyPath(data.email_verification?.verify_path || null);
    } catch (e) { setErr(formatApiError(e)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10" data-testid="register-page">
      <div className="max-w-3xl mx-auto px-4">
        <Link to="/" className="flex items-center gap-2 mb-6" data-testid="register-brand-link">
          <div className="w-9 h-9 rounded-md bg-brand-navy flex items-center justify-center">
            <Factory className="w-5 h-5 text-brand-amber" />
          </div>
          <div>
            <div className="font-bold text-brand-navy">MERQAV</div>
            <div className="text-[11px] text-slate-500">MERQAV</div>
          </div>
        </Link>

        <h1 className="text-3xl font-bold text-brand-navy mb-2">إنشاء حساب</h1>
        <p className="text-slate-600 mb-6">اختر نوع الحساب المناسب لك وأكمل بياناتك.</p>

        {registeredEmail && (
          <div className="bg-white border-2 border-emerald-500 rounded-lg p-6 mb-6" data-testid="verify-email-panel">
            <div className="text-emerald-700 font-bold text-lg mb-2">✓ تم إنشاء الحساب بنجاح</div>
            <p className="text-slate-700 mb-3">أرسلنا رابط تفعيل إلى <span className="font-semibold">{registeredEmail}</span>. يرجى فتح الرابط لتفعيل بريدك الإلكتروني.</p>
            {verifyPath && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-slate-700">
                <div className="font-semibold text-amber-800 mb-1">وضع التطوير:</div>
                يمكنك النقر مباشرة على الرابط أدناه للتفعيل:{" "}
                <Link to={verifyPath} className="text-brand-amber font-semibold underline" data-testid="dev-verify-link">
                  تفعيل الحساب الآن
                </Link>
              </div>
            )}
            <Link to="/app" className="block mt-4">
              <Button variant="outline" className="w-full" data-testid="skip-to-app-btn">الانتقال إلى لوحة التحكم</Button>
            </Link>
          </div>
        )}

        {/* Role selector */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setRole("factory_customer")}
            className={`card-industrial p-5 text-start ${role === "factory_customer" ? "border-brand-amber ring-2 ring-brand-amber/30" : ""}`}
            data-testid="role-factory-btn"
          >
            <Factory className="w-6 h-6 text-brand-amber mb-2" />
            <div className="font-bold text-brand-navy">مصنع / شركة</div>
            <div className="text-xs text-slate-500 mt-1">أحتاج قطع صناعية أو خدمات تصنيع</div>
          </button>
          <button
            type="button"
            onClick={() => setRole("workshop_partner")}
            className={`card-industrial p-5 text-start ${role === "workshop_partner" ? "border-brand-amber ring-2 ring-brand-amber/30" : ""}`}
            data-testid="role-workshop-btn"
          >
            <Wrench className="w-6 h-6 text-brand-amber mb-2" />
            <div className="font-bold text-brand-navy">ورشة تصنيع</div>
            <div className="text-xs text-slate-500 mt-1">أقدم خدمات تصنيع للمصانع</div>
          </button>
        </div>

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="register-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>الاسم بالكامل</Label>
              <Input required value={f.name} onChange={(e) => upd("name", e.target.value)} className="mt-1.5" data-testid="reg-name" />
            </div>
            {role === "factory_customer" && (
              <div>
                <Label>المسمى الوظيفي</Label>
                <Input value={f.job_title} onChange={(e) => upd("job_title", e.target.value)} className="mt-1.5" data-testid="reg-job-title" />
              </div>
            )}
            <div>
              <Label>البريد الإلكتروني</Label>
              <Input required type="email" value={f.email} onChange={(e) => onEmailChange(e.target.value)} className="mt-1.5" data-testid="reg-email" />
              {emailType === "personal" && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800" data-testid="email-personal-hint">
                  ننصح باستخدام البريد الرسمي للمنشأة (مثال: info@company.sa) لزيادة مستوى التحقق.
                </div>
              )}
              {emailType === "business" && (
                <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded-md p-2 text-emerald-800" data-testid="email-business-hint">
                  ✓ بريد رسمي — سيرفع مستوى التحقق تلقائياً.
                </div>
              )}
            </div>
            <div>
              <Label>رقم الجوال</Label>
              <Input required value={f.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+9665..." className="mt-1.5" data-testid="reg-phone" />
            </div>
            <div>
              <Label>{role === "factory_customer" ? "اسم الشركة / المصنع" : "اسم الورشة"}</Label>
              <Input required
                value={role === "factory_customer" ? f.company_name : f.workshop_name}
                onChange={(e) => upd(role === "factory_customer" ? "company_name" : "workshop_name", e.target.value)}
                className="mt-1.5" data-testid="reg-company-name" />
            </div>
            <div>
              <Label>رقم السجل التجاري</Label>
              <Input required value={f.cr_number} onChange={(e) => upd("cr_number", e.target.value)} className="mt-1.5" data-testid="reg-cr-number" />
            </div>
            <div>
              <Label>الرقم الضريبي</Label>
              <Input value={f.vat_number} onChange={(e) => upd("vat_number", e.target.value)} className="mt-1.5" data-testid="reg-vat-number" />
            </div>
            {role === "factory_customer" && (
              <div>
                <Label>القطاع الصناعي</Label>
                <Select value={f.industry} onValueChange={(v) => upd("industry", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="reg-industry-select"><SelectValue placeholder="اختر القطاع" /></SelectTrigger>
                  <SelectContent>
                    {industries.map((i) => <SelectItem key={i.key} value={i.key}>{i.label_ar}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>المدينة</Label>
              <Select value={f.city} onValueChange={(v) => upd("city", v)}>
                <SelectTrigger className="mt-1.5" data-testid="reg-city-select"><SelectValue placeholder="اختر المدينة" /></SelectTrigger>
                <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>المنطقة</Label>
              <Select value={f.region} onValueChange={(v) => upd("region", v)}>
                <SelectTrigger className="mt-1.5" data-testid="reg-region-select"><SelectValue placeholder="اختر المنطقة" /></SelectTrigger>
                <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {role === "workshop_partner" && (
              <>
                <div>
                  <Label>المنطقة الصناعية</Label>
                  <Input value={f.industrial_area} onChange={(e) => upd("industrial_area", e.target.value)} className="mt-1.5" data-testid="reg-industrial-area" />
                </div>
                <div>
                  <Label>سنة التأسيس</Label>
                  <Input type="number" value={f.year_established} onChange={(e) => upd("year_established", e.target.value)} className="mt-1.5" data-testid="reg-year-established" />
                </div>
                <div>
                  <Label>عدد الموظفين</Label>
                  <Input type="number" value={f.employees} onChange={(e) => upd("employees", e.target.value)} className="mt-1.5" data-testid="reg-employees" />
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <Label>كلمة المرور (6 أحرف على الأقل)</Label>
              <Input required type="password" value={f.password} onChange={(e) => upd("password", e.target.value)} className="mt-1.5" data-testid="reg-password" />
            </div>
          </div>

          {role === "workshop_partner" && (
            <div>
              <Label>القدرات التصنيعية (اختر ما ينطبق)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 max-h-64 overflow-y-auto p-3 border border-slate-200 rounded-md bg-slate-50">
                {capabilities.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={f.capabilities.includes(c.key)} onCheckedChange={() => toggleCap(c.key)} data-testid={`reg-cap-${c.key}`} />
                    <span>{c.label_ar}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid="reg-agree-label">
            <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="reg-agree-checkbox" />
            <span>أوافق على <Link to="/terms" className="text-brand-amber hover:underline">الشروط والأحكام</Link>، و<Link to="/privacy" className="text-brand-amber hover:underline">سياسة الخصوصية</Link>، و<Link to="/confidentiality" className="text-brand-amber hover:underline">سياسة السرية</Link>.</span>
          </label>

          <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="reg-attest-label">
            <Checkbox checked={attest} onCheckedChange={setAttest} data-testid="reg-attest-checkbox" />
            <span className="leading-relaxed">
              أقر بأن جميع البيانات المدخلة صحيحة، وأتحمل مسؤولية صحتها، ويحق لمنصة MERQAV مراجعة البيانات
              أو تعليق الحساب في حال ثبوت عدم صحتها.
            </span>
          </label>

          {err && <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3" data-testid="reg-error">{err}</div>}

          <Button type="submit" disabled={loading} className="btn-cta w-full h-11" data-testid="reg-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "إنشاء الحساب"}
          </Button>

          <div className="text-center text-sm text-slate-600">
            لديك حساب؟ <Link to="/login" className="text-brand-amber font-semibold" data-testid="goto-login-link">تسجيل الدخول</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
